import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { assessmentRequestKey, DraftApiError, DraftSaveQueue, loadCurrentDraft, loadDraft, lookupVehicle, startAssessment } from './draftApi'
import type { DraftPayload, SavedDraft, SaveState } from './draftApi'

type Values = { rego: string; state: string; kilometres: string; year: string; make: string; model: string; name: string; email: string }
type Field = keyof Values
const initialValues: Values = { rego: '', state: '', kilometres: '', year: '', make: '', model: '', name: '', email: '' }
const states = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']
const validEmail = (value: string) => value.trim().length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
const validKilometres = (value: string) => /^\d+$/.test(value) && Number(value) <= 9999999
const validYear = (value: string) => /^\d{4}$/.test(value) && Number(value) >= 1900 && Number(value) <= new Date().getFullYear() + 1
const price = (value: string) => Number(value).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })

function valuesFromDraft(draft: SavedDraft | null): Values {
  if (!draft) return { ...initialValues }
  return Object.fromEntries(Object.keys(initialValues).map(field => [field, String(draft[field as Field] ?? '')])) as Values
}

function saveCandidate(values: Values, manual: boolean): DraftPayload | null {
  if (!validEmail(values.email) || values.name.trim().length > 120) return null
  if (values.kilometres && !validKilometres(values.kilometres)) return null
  if (manual) {
    if (!validYear(values.year) || !values.make.trim() || values.make.trim().length > 80 || !values.model.trim() || values.model.trim().length > 100) return null
  } else if (!values.rego.trim() || values.rego.length > 15 || !states.includes(values.state)) return null
  return {
    entry_mode: manual ? 'manual' : 'rego',
    rego: manual ? null : values.rego.trim().toUpperCase(),
    state: manual ? null : values.state,
    kilometres: values.kilometres === '' ? null : Number(values.kilometres),
    year: manual ? Number(values.year) : null,
    make: manual ? values.make.trim() : null,
    model: manual ? values.model.trim() : null,
    name: values.name.trim() || null,
    email: values.email.trim().toLowerCase(),
  }
}

export function EnquiryPreview() {
  const [step, setStep] = useState(0)
  const [manual, setManual] = useState(false)
  const [values, setValues] = useState<Values>({ ...initialValues })
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [hydrated, setHydrated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [startingAssessment, setStartingAssessment] = useState(false)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)
  const [lookupMessage, setLookupMessage] = useState<string | null>(null)
  const [lookingUp, setLookingUp] = useState(false)
  const lookupRequest = useRef<{ fingerprint: string; key: string } | null>(null)
  const startRequest = useRef<string | null>(null)
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle', draft: null, error: null })
  const headingRef = useRef<HTMLHeadingElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const shouldFocus = useRef(false)
  const mounted = useRef(false)
  const loader = useRef<AbortController | null>(null)
  const loadAttempt = useRef(0)
  const saver = useRef<DraftSaveQueue | null>(null)
  const candidate = useMemo(() => saveCandidate(values, manual), [values, manual])
  const conflicted = saveState.status === 'conflict'
  const assessmentReady = saveState.draft?.assessment_prepared && saveState.draft.assessment_status !== 'cancelled'

  const restoreSaved = useCallback(async (resume = false, recover = false) => {
    const previousDraft = saver.current?.confirmedDraft()
    const attempt = ++loadAttempt.current
    loader.current?.abort()
    const controller = new AbortController()
    loader.current = controller
    setLoading(true)
    setLoadError(null)
    try {
      const draft = resume || recover ? await loadCurrentDraft() : await loadDraft(controller.signal)
      if (recover && (!draft || previousDraft && (draft.id !== previousDraft.id || draft.version < previousDraft.version))) {
        throw new Error('Your saved enquiry could not be recovered. Your entered details are still here. Please retry.')
      }
      if (!mounted.current || attempt !== loadAttempt.current) return
      saver.current?.dispose()
      saver.current = new DraftSaveQueue(draft, setSaveState)
      setValues(valuesFromDraft(draft))
      setManual(draft?.entry_mode === 'manual')
      setSaveState({ status: draft ? 'saved' : 'idle', draft, error: null })
      setHasChanges(false)
      setErrors({})
      setLookupMessage(null)
      lookupRequest.current = null
      startRequest.current = null
      setAssessmentError(null)
      if (!recover) setStep(resume && draft?.details_complete ? 2 : 0)
      setHydrated(true)
    } catch (error) {
      if (mounted.current && attempt === loadAttempt.current) setLoadError(error instanceof Error ? error.message : 'Your saved details could not be loaded. Please retry.')
    } finally {
      if (mounted.current && attempt === loadAttempt.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    void restoreSaved(new URLSearchParams(window.location.search).get('resume') === '1')
    return () => { mounted.current = false; loader.current?.abort(); saver.current?.dispose() }
  }, [restoreSaved])

  useEffect(() => {
    if (!hydrated || loading || !hasChanges || !saver.current) return
    const queue = saver.current
    queue.observe(candidate)
    if (!candidate) return
    const timer = window.setTimeout(() => { void queue.save(candidate) }, 700)
    return () => window.clearTimeout(timer)
  }, [candidate, hydrated, loading, hasChanges])

  useEffect(() => {
    const serverErrors = saveState.error?.errors
    if (!serverErrors) return
    const fieldErrors: Partial<Record<Field, string>> = {}
    Object.entries(serverErrors).forEach(([field, messages]) => {
      if (field in initialValues) fieldErrors[field as Field] = Array.isArray(messages) ? messages.join(', ') : messages
    })
    setErrors(previous => ({ ...previous, ...fieldErrors }))
  }, [saveState.error])

  useEffect(() => {
    if (!shouldFocus.current) return
    if (Object.keys(errors).length) formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
    else headingRef.current?.focus()
    shouldFocus.current = false
  }, [step, errors, manual])

  const update = (field: Field, value: string) => {
    setValues(previous => ({ ...previous, [field]: value }))
    setHasChanges(true)
    setErrors(previous => { const next = { ...previous }; delete next[field]; return next })
  }

  const next = async (event: React.FormEvent) => {
    event.preventDefault()
    if (loading || finishing || step === 1 && conflicted) return
    const nextErrors: Partial<Record<Field, string>> = {}
    if (manual) {
      if (!validYear(values.year)) nextErrors.year = 'Enter a valid four-digit year.'
      if (!values.make.trim()) nextErrors.make = 'Enter the make of your car.'
      if (!values.model.trim()) nextErrors.model = 'Enter the model of your car.'
    } else {
      if (!values.rego.trim()) nextErrors.rego = 'Enter your registration plate.'
      if (!states.includes(values.state)) nextErrors.state = 'Select your registration state or territory.'
    }
    if (!validKilometres(values.kilometres)) nextErrors.kilometres = 'Enter whole kilometres from 0 to 9,999,999.'
    if (step === 1) {
      if (!values.name.trim()) nextErrors.name = 'Enter your name.'
      if (!validEmail(values.email)) nextErrors.email = 'Enter a valid email address.'
    }
    shouldFocus.current = true
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    if (step === 0) { setStep(1); return }
    if (!candidate || !saver.current) return
    setFinishing(true)
    try {
      const confirmed = await saver.current.save(candidate)
      if (mounted.current && confirmed) {
        await performLookup()
        if (mounted.current) { shouldFocus.current = true; setStep(2) }
      }
    } finally { if (mounted.current) setFinishing(false) }
  }

  const back = () => { shouldFocus.current = true; setErrors({}); setStep(previous => Math.max(0, previous - 1)) }
  const chooseMode = (isManual: boolean) => { setManual(isManual); setHasChanges(true); setErrors({}) }
  const performLookup = async () => {
    const queue = saver.current
    const draft = queue?.confirmedDraft()
    if (!draft || !queue) return
    setLookingUp(true)
    setLookupMessage(null)
    const fingerprint = JSON.stringify([draft.entry_mode, draft.rego, draft.state, draft.kilometres, draft.year, draft.make, draft.model])
    if (lookupRequest.current?.fingerprint !== fingerprint) lookupRequest.current = { fingerprint, key: assessmentRequestKey() }
    try {
      let result
      try {
        result = await lookupVehicle(draft, lookupRequest.current!.key)
      } catch (error) {
        if (!(error instanceof DraftApiError) || error.code !== 'stale_version') throw error
        const current = await loadCurrentDraft()
        // A staff-only update or completed lookup can advance the version while
        // this page is open. Never adopt different customer-entered details.
        if (!current || !queue.acceptLookup(current)) throw error
        lookupRequest.current = { fingerprint, key: assessmentRequestKey() }
        result = await lookupVehicle(current, lookupRequest.current.key)
      }
      if (!mounted.current) return
      if (!queue.acceptLookup(result.data)) {
        setLookupMessage('Your details changed during the lookup. Please check them and save again.')
      } else {
        setLookupMessage(result.lookup.status === 'succeeded'
          ? result.data.assessment_prepared ? 'Vehicle identified. Your assessment has been prepared with these details.' : 'Vehicle identified. Please retry to finish preparing your assessment.'
          : result.lookup.status === 'sending' ? 'Vehicle lookup is still processing. Your enquiry is saved; please retry shortly.'
          : 'Your enquiry is saved. The team can check your vehicle manually; you can continue your assessment.')
        // A completed failure is safe to retry as a new attempt; ambiguous failures retain their key.
        if (['failed', 'no_match'].includes(result.lookup.status) || ['failed', 'no_match'].includes(result.lookup.pricing_status)) lookupRequest.current = null
      }
    } catch (error) {
      if (mounted.current) setLookupMessage(error instanceof Error ? error.message : 'Your enquiry is saved. Please retry the vehicle lookup.')
    } finally { if (mounted.current) setLookingUp(false) }
  }
  const continueToAssessment = async () => {
    if (startingAssessment || saveState.status !== 'saved') return
    setStartingAssessment(true)
    setAssessmentError(null)
    try {
      startRequest.current ||= assessmentRequestKey()
      const assessmentUrl = await startAssessment(startRequest.current)
      window.location.assign(assessmentUrl)
    } catch (error) {
      if (mounted.current) {
        setAssessmentError(error instanceof Error ? error.message : 'Please retry starting your assessment.')
        setStartingAssessment(false)
      }
    }
  }
  const input = (field: Field, label: string, options: { placeholder?: string; inputMode?: 'numeric' | 'email'; autoComplete?: string; type?: string; maxLength?: number } = {}) => <div className="cs-field">
    <label className="form-label" htmlFor={`acquisition-${field}`}>{label}</label>
    <input id={`acquisition-${field}`} className={`form-control${errors[field] ? ' is-invalid' : ''}`} value={values[field]} onChange={event => update(field, field === 'rego' ? event.target.value.toUpperCase() : event.target.value)} type={options.type || 'text'} inputMode={options.inputMode} autoComplete={options.autoComplete || 'off'} placeholder={options.placeholder} maxLength={options.maxLength} aria-invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `acquisition-${field}-error` : undefined} required />
    {errors[field] && <p className="invalid-feedback" id={`acquisition-${field}-error`}>{errors[field]}</p>}
  </div>

  const statusText = loading ? 'Loading saved details…' : saveState.status === 'saving' ? 'Saving…'
    : saveState.status === 'saved' ? 'Saved'
    : saveState.status === 'conflict' ? 'Unsaved changes · Review needed'
    : saveState.status === 'error' || saveState.status === 'unsaved' || hasChanges ? 'Unsaved changes'
    : 'Your enquiry is ready to start'

  return <>
    <div className="cs-form-heading"><span className="cs-eyebrow">LET’S START WITH YOUR CAR</span><span className="cs-preview-tag">Development</span></div>
    <ol className="cs-form-steps" aria-label="Enquiry progress">
      {['Your car', 'Your details', 'Your price'].map((label, index) => <li key={label} className={index <= step ? 'cs-step-active' : ''} aria-current={index === step ? 'step' : undefined}><span>{index < step ? '✓' : index + 1}</span>{label}</li>)}
    </ol>
    <h2 className="cs-form-title" id="acquisition-form-title" ref={headingRef} tabIndex={-1}>{['What are you driving?', 'A little about you.', 'Your estimated purchase range'][step]}</h2>
    {step < 2 && <p id="acquisition-collection-notice" className="cs-form-fineprint">Enter your details to get your estimated purchase range. <a href="/privacy">Privacy information</a>.</p>}
    {!hydrated ? <div className="py-3" aria-live="polite">{loading ? <p className="cs-form-intro mb-0">Loading your enquiry…</p> : <><p className="cs-error-summary">{loadError}</p><button className="btn cs-btn-outline w-100 mt-3" type="button" onClick={() => { void restoreSaved() }}>Retry loading details</button></>}</div> : <>
      {step < 2 ? <form ref={formRef} onSubmit={event => { void next(event) }} aria-describedby="acquisition-collection-notice" noValidate>
        <fieldset disabled={loading || finishing}>
          {step === 0 ? <>
            <p className="cs-form-intro">Start with your car’s registration and odometer reading, or enter its details manually.</p>
            <div className="cs-form-switch" role="group" aria-label="How would you like to enter your car?">
              <button type="button" className={!manual ? 'cs-selected' : ''} aria-pressed={!manual} onClick={() => chooseMode(false)}>Use registration</button>
              <button type="button" className={manual ? 'cs-selected' : ''} aria-pressed={manual} onClick={() => chooseMode(true)}>Enter car details</button>
            </div>
            {manual ? <div className="row g-3"><div className="col-4">{input('year', 'Year', { placeholder: '2020', inputMode: 'numeric', maxLength: 4 })}</div><div className="col-8">{input('make', 'Make', { placeholder: 'e.g. Toyota', maxLength: 80 })}</div><div className="col-12">{input('model', 'Model', { placeholder: 'e.g. RAV4', maxLength: 100 })}</div></div> : <div className="row g-3">
              <div className="col-7">{input('rego', 'Registration plate', { placeholder: 'ABC123', maxLength: 15 })}</div>
              <div className="col-5 cs-field"><label className="form-label" htmlFor="acquisition-state">State / territory</label><select id="acquisition-state" className={`form-select${errors.state ? ' is-invalid' : ''}`} value={values.state} onChange={event => update('state', event.target.value)} aria-invalid={Boolean(errors.state)} aria-describedby={errors.state ? 'acquisition-state-error' : undefined} required><option value="">Select state</option>{states.map(state => <option key={state} value={state}>{state}</option>)}</select>{errors.state && <p className="invalid-feedback" id="acquisition-state-error">{errors.state}</p>}</div>
            </div>}
            <div className="mt-3">{input('kilometres', 'Kilometres on the odometer', { placeholder: 'e.g. 65000', inputMode: 'numeric', maxLength: 7 })}</div>
            <button type="submit" className="btn cs-btn-primary w-100 mt-4">Continue <span aria-hidden="true">→</span></button>
          </> : <>
            <p className="cs-form-intro">Add your name and email to get your estimated purchase range.</p>
            {input('name', 'Your name', { placeholder: 'e.g. Alex Taylor', autoComplete: 'name', maxLength: 120 })}
            <div className="mt-3">{input('email', 'Email address', { placeholder: 'alex@example.com', autoComplete: 'email', type: 'email', inputMode: 'email', maxLength: 254 })}</div>
            <p className="cs-form-fineprint mt-3">Get your estimated purchase range, then choose whether to complete a self-assessment.</p>
            <button type="submit" className="btn cs-btn-primary w-100 mt-3" disabled={conflicted}>{finishing ? 'Getting your price…' : 'Get my price'} <span aria-hidden="true">→</span></button>
            <button type="button" className="cs-back-button" onClick={back}>← Back to your car</button>
          </>}
        </fieldset>
        {Object.keys(errors).length > 0 && <p className="cs-error-summary" role="alert">Please check the highlighted fields to continue.</p>}
      </form> : <div className="cs-example-result">
        <p className="cs-form-fineprint">Your enquiry is saved · {saveState.draft?.reference}</p>
        {saveState.draft?.vehicle?.make && <p className="cs-form-intro"><strong>{[saveState.draft.vehicle.year, saveState.draft.vehicle.make, saveState.draft.vehicle.model, saveState.draft.vehicle.badge].filter(Boolean).join(' ')}</strong> · {Number(saveState.draft.kilometres || 0).toLocaleString('en-AU')} km</p>}
        {saveState.draft?.estimated_range ? <div className="cs-price-example" role="status" aria-label="Estimated purchase range">
          <span>Estimated purchase range · AUD</span>
          <strong>{price(saveState.draft.estimated_range.lower)} – {price(saveState.draft.estimated_range.upper)}</strong>
          <p>Based on your car and kilometres. Final price depends on its condition and the buying team’s review.</p>
        </div> : <p className="cs-form-intro">{lookingUp ? 'Calculating your estimated purchase range…' : 'The team will need to review your car for pricing. Your enquiry is saved.'}</p>}
        {lookupMessage && <p className="cs-form-fineprint" role="status">{lookupMessage}</p>}
        <h3 className="cs-assessment-invite">Would you like to proceed?</h3>
        <p className="cs-form-fineprint">{assessmentReady ? 'Your self-assessment is ready. Continue to add your car’s condition and photos. We’ll also email you a private link, valid for seven days. Check your spam or junk folder if it doesn’t arrive.' : 'Please retry to finish preparing your self-assessment. Your saved enquiry is safe.'}</p>
        <button type="button" className="btn cs-btn-primary w-100" disabled={!assessmentReady || startingAssessment || lookingUp || saveState.status !== 'saved'} onClick={() => { void continueToAssessment() }}>{startingAssessment ? 'Opening assessment…' : 'Yes, continue to my assessment'} <span aria-hidden="true">→</span></button>
        {(!assessmentReady || !manual && (saveState.draft?.vehicle_lookup_status !== 'matched' || ['failed', 'no_match', 'sending'].includes(saveState.draft?.pricing_status || ''))) && <button type="button" className="btn cs-btn-outline w-100 mt-2" disabled={lookingUp || startingAssessment || saveState.status !== 'saved'} onClick={() => { void performLookup() }}>{lookingUp ? 'Looking up your car…' : assessmentReady ? 'Retry vehicle lookup' : 'Retry preparing assessment'}</button>}
        {assessmentError && <p className="cs-error-summary mt-3" role="alert">{assessmentError}</p>}
        <button type="button" className="cs-back-button" disabled={startingAssessment} onClick={back}>← Back to your details</button>
        <button type="button" className="btn cs-btn-outline w-100 mt-2" disabled={lookingUp || startingAssessment || loading} onClick={() => { void restoreSaved() }}>Start a new enquiry</button>
      </div>}
      {conflicted && <div className="alert alert-warning mt-3 mb-0" role="alert"><p className="small mb-2">This enquiry has changed elsewhere. Your entered details are still here. Loading the saved details will replace these unsaved changes.</p><button type="button" className="btn btn-sm btn-outline-dark" disabled={loading} onClick={() => { void restoreSaved(false, true) }}>{loading ? 'Loading…' : 'Load saved details'}</button></div>}
      {saveState.status === 'error' && <div className="alert alert-warning mt-3 mb-0" role="alert"><p className="small mb-2">{saveState.error?.message || 'Your changes have not been saved.'}</p>{saveState.error?.code === 'invalid_enquiry' ? <p className="small mb-0">Correct the details above to save again.</p> : <button type="button" className="btn btn-sm btn-outline-dark" disabled={loading} onClick={() => { void saver.current?.retry() }}>Retry save</button>}</div>}
      {loadError && <p className="cs-error-summary" role="alert">{loadError}</p>}
    </>}
    <div className="cs-form-footer" role="status" aria-live="polite"><svg width="14" height="16" viewBox="0 0 16 18" fill="none" aria-hidden="true"><rect x="2" y="8" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 8V5a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.5"/></svg><span>{statusText}</span></div>
  </>
}
